import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import axios from 'axios';
import {
  ChevronRight, ChevronLeft, ArrowRight, Loader2,
  Bot, BrainCircuit, MessageSquare, Mic, Network, Sliders,
  Building2, ShoppingCart, HeartPulse, Scale, Briefcase, GraduationCap,
  Zap, ShieldCheck, Globe, Cpu, CheckCircle2,
  Database, Search, Layers, Code2, Smartphone, Palette, TestTube2,
  FileText, BarChart3, Settings, Users, Lock, Repeat, Workflow,
  PenTool, Monitor, Server, Cloud, Truck, Boxes,
} from 'lucide-react';

import PageHero from '@/components/layout/PageHero';
import SectionHeading from '@/components/brand/SectionHeading';
import MetricCountUp from '@/components/brand/MetricCountUp';
import CtaBand from '@/components/sections/CtaBand';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, staggerParent, cardPop, viewportOnce } from '@/lib/motion';
import { useServiceDetail } from '@/hooks/useServiceDetail';
import NotFound from '@/pages/NotFound';

const EASE = [0.22, 1, 0.36, 1];
const BASE = process.env.REACT_APP_BACKEND_URL || '';
const MotionLink = motion.create(Link);

function BlueAnimatedLink({ to, children }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 220, damping: 18, mass: 0.5 });
  const y = useSpring(my, { stiffness: 220, damping: 18, mass: 0.5 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(Math.max(-6, Math.min(6, (e.clientX - (r.left + r.width / 2)) * 0.18)));
    my.set(Math.max(-4, Math.min(4, (e.clientY - (r.top + r.height / 2)) * 0.22)));
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return (
    <MotionLink
      to={to}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 360, damping: 22 }}
      className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-7 py-3 text-sm font-semibold text-white bg-brandblue hover:bg-[#1a4d80] shadow-[0_4px_20px_rgba(47,111,176,0.40)] hover:shadow-[0_18px_44px_rgba(47,111,176,0.50)] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue/60"
    >
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-full bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.28)_50%,transparent_75%)] opacity-0 group-hover:opacity-100 group-hover:animate-[sheen_900ms_ease-in-out]"
      />
    </MotionLink>
  );
}

/* ─── Icon map ───────────────────────────────────────────────────────────── */
const ICON_MAP = {
  Bot, BrainCircuit, MessageSquare, Mic, Network, Sliders,
  Building2, ShoppingCart, HeartPulse, Scale, Briefcase, GraduationCap,
  Zap, ShieldCheck, Globe, Cpu, CheckCircle2,
  Database, Search, Layers, Code2, Smartphone, Palette, TestTube2,
  FileText, BarChart3, Settings, Users, Lock, Repeat, Workflow,
  PenTool, Monitor, Server, Cloud, Truck, Boxes,
};

function resolveIcon(name, fallback = Bot) {
  return ICON_MAP[name] || fallback;
}

/* ─── Sub-components ────────────────────────────────────────────────────── */

function MetricsBar({ metrics }) {
  return (
    <section className="bg-white border-b border-hairline">
      <div className="container mx-auto">
        <motion.div
          variants={staggerParent(0.1, 0)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-hairline"
        >
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              variants={cardPop}
              className="px-6 py-8 text-center group"
            >
              <div className="text-4xl sm:text-5xl font-bold text-navy-deep leading-none">
                <MetricCountUp value={m.value} />
              </div>
              <div className="mt-2 text-sm text-slatebody leading-snug">{m.label}</div>
              <div className="mx-auto mt-3 h-0.5 w-0 group-hover:w-10 bg-gold-gradient transition-all duration-500 rounded-full" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function OverviewSection({ heading, body, image }) {
  const paragraphs = (body || '').split('\n\n').filter(Boolean);
  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <SectionHeading
            eyebrow="What We Do"
            title={heading}
            align="left"
          />
          <div className="mt-8 space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-slatebody leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-8">
            <PrimaryButton icon={<ArrowRight className="h-4 w-4" />} onClick={() => {
              document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              Start Your AI Project
            </PrimaryButton>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="relative"
        >
          <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-brandblue/10 to-[#E8C25A]/10 blur-2xl" />
          <img
            src={image}
            alt="Generative AI illustration"
            className="relative rounded-2xl shadow-[0_24px_64px_rgba(47,111,176,0.18)] w-full object-cover aspect-[4/3]"
            loading="lazy"
          />
          <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-card px-4 py-3 flex items-center gap-2 border border-hairline">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-navy-deep">AI Systems Live in Production</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FeaturesSection({ features }) {
  return (
    <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(150deg,#0F192D 0%,#1F2D45 60%,#18304E 100%)' }}>
      <div className="absolute inset-0 opacity-[0.06]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="featdots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="#fff" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#featdots)" />
        </svg>
      </div>

      <div className="container mx-auto relative">
        <SectionHeading
          eyebrow="Our Capabilities"
          title="What We Build For You"
          subtitle="Six core AI engineering capabilities — each battle-tested in production environments across 40+ countries."
          light
        />

        <motion.div
          variants={staggerParent(0.08, 0.15)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {features.map((f, i) => {
            const Icon = resolveIcon(f.icon);
            const accent = f.accent || '#2F6FB0';
            return (
              <motion.div
                key={i}
                variants={cardPop}
                className="group relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-white/25 transition-all duration-300 overflow-hidden"
              >
                <div
                  className="absolute top-0 inset-x-0 h-0.5 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(90deg, ${accent}, ${accent}88)` }}
                />
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${accent}25` }}
                >
                  <Icon className="h-5 w-5" style={{ color: accent }} />
                </div>
                <h3 className="text-white font-semibold text-base leading-snug mb-2">{f.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#C9962E] hover:bg-[#b8851f] text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:shadow-[0_6px_20px_rgba(201,150,46,0.45)] hover:-translate-y-0.5"
          >
            Discuss this with us <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full border-2 border-white/60 hover:border-white text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300 hover:bg-white/10"
          >
            View All Services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function ProcessSection({ steps }) {
  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="How We Work"
          title="Our AI Delivery Process"
          subtitle="A structured, transparent process — from the first whiteboard session to a live AI system generating value for your business."
          align="center"
        />

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-hairline to-transparent" />
          {steps.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              transition={{ delay: i * 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-brandblue to-[#1a4d80] flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_rgba(47,111,176,0.35)]">
                  <span className="text-white font-bold text-xl">{s.step}</span>
                </div>
              </div>
              <h3 className="text-navy-deep font-bold text-lg mb-2">{s.title}</h3>
              <p className="text-slatebody text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            type="button"
            onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-[#1a2d45] text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:shadow-[0_6px_20px_rgba(31,45,69,0.45)] hover:-translate-y-0.5"
          >
            Start your project <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 rounded-full border-2 border-navy-deep hover:bg-navy-deep text-navy-deep hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            See Our Portfolio <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function UseCasesSection({ useCases }) {
  return (
    <section className="py-20 bg-sky-50">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Industry Applications"
          title="Where Generative AI Creates Real Impact"
          subtitle="From customer-facing chatbots to back-office automation — AI that pays for itself."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.07, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {useCases.map((u, i) => {
            const Icon = resolveIcon(u.icon, Briefcase);
            return (
              <Link
                key={i}
                to="/contact"
                className="group bg-white rounded-2xl border border-hairline p-6 shadow-card hover:shadow-[0_12px_40px_rgba(47,111,176,0.12)] hover:-translate-y-1 transition-all duration-300 block"
              >
                <motion.div variants={cardPop}>
                  <div className="h-10 w-10 rounded-xl bg-brandblue/10 flex items-center justify-center mb-4 group-hover:bg-brandblue/20 transition-colors duration-300">
                    <Icon className="h-5 w-5 text-brandblue" />
                  </div>
                  <h3 className="text-navy-deep font-semibold text-base mb-2">{u.industry}</h3>
                  <p className="text-slatebody text-sm leading-relaxed">{u.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-brandblue group-hover:text-[#C9962E] transition-colors">
                    Explore this use case <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                </motion.div>
              </Link>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 text-center"
        >
          <Link
            to="/industries"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brandblue hover:bg-brandblue text-brandblue hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            Explore All Industries <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function TechStackSection({ techStack }) {
  return (
    <section className="py-16 bg-white border-y border-hairline overflow-hidden">
      <div className="container mx-auto">
        <p className="text-center text-xs uppercase tracking-[0.2em] font-semibold text-brandblue mb-8">
          Technologies & Frameworks We Work With
        </p>
        <motion.div
          variants={staggerParent(0.05, 0)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="flex flex-wrap justify-center gap-3"
        >
          {techStack.map((t, i) => (
            <motion.span
              key={i}
              variants={cardPop}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold text-navy-deep transition-all duration-300 hover:-translate-y-0.5 cursor-default"
              style={{ borderColor: `${t.color}44`, background: `${t.color}0f` }}
            >
              <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
              {t.name}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function BenefitsSection({ benefits }) {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Why MGS"
          title="What Sets Our AI Engineering Apart"
          subtitle="We are not an AI consultancy that hands you a slide deck. We are the engineers who ship it."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.08, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {benefits.map((b, i) => {
            const Icon = resolveIcon(b.icon, CheckCircle2);
            const accentColors = ['#2F6FB0', '#C9962E', '#059669', '#7C3AED'];
            const accent = accentColors[i % accentColors.length];
            return (
              <motion.div
                key={i}
                variants={cardPop}
                className="group rounded-2xl border border-hairline p-6 text-center hover:shadow-card transition-all duration-300 hover:-translate-y-1"
              >
                <div
                  className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${accent}15` }}
                >
                  <Icon className="h-6 w-6" style={{ color: accent }} />
                </div>
                <h3 className="text-navy-deep font-bold text-base mb-2">{b.title}</h3>
                <p className="text-slatebody text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTAs */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/why-mgs"
            className="inline-flex items-center gap-2 rounded-full border-2 border-navy-deep hover:bg-navy-deep text-navy-deep hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            About Max Global Software <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/our-team"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brandblue hover:bg-brandblue text-brandblue hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            Meet Our Team <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Related Services section ──────────────────────────────────────────── */
function RelatedServicesSection({ currentSlug }) {
  const [related, setRelated] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BASE}/api/v1/collections/servicePages`)
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.data ?? [];
        setRelated(
          items
            .filter((svc) => svc.slug !== currentSlug)
            .slice(0, 3)
            .map((svc) => ({
              slug: svc.slug,
              title: svc.title || svc.slug,
            }))
        );
      })
      .catch(() => { if (!cancelled) setRelated([]); });
    return () => { cancelled = true; };
  }, [currentSlug]);

  if (!Array.isArray(related) || related.length === 0) return null;

  return (
    <section className="py-20 bg-sky-50 border-t border-hairline">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Explore More"
          title="Related Services"
          subtitle="We offer end-to-end technology capabilities. Explore how our other services can support your goals."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.1, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {related.map((svc) => {
            const Icon = resolveIcon(svc.icon, ArrowRight);
            return (
              <motion.div key={svc.slug} variants={cardPop}>
                <Link
                  to={`/services/${svc.slug}`}
                  className="group flex flex-col h-full bg-white rounded-2xl border border-hairline p-7 shadow-card hover:shadow-[0_16px_48px_rgba(47,111,176,0.13)] hover:-translate-y-1.5 transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-2xl bg-brandblue/10 flex items-center justify-center mb-5 group-hover:bg-brandblue/20 transition-colors duration-300">
                    <Icon className="h-6 w-6 text-brandblue" />
                  </div>
                  <h3 className="text-navy-deep font-bold text-lg leading-snug mb-3 flex-1">{svc.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slatebody group-hover:text-[#C9962E] transition-colors mt-auto">
                    Explore service <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-10 text-center"
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-[#1a2d45] text-white text-sm font-semibold px-8 py-3 transition-all duration-300 hover:shadow-[0_8px_24px_rgba(15,25,45,0.35)] hover:-translate-y-0.5"
          >
            View All 12 Services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: cmsData, loading, notFound } = useServiceDetail(slug);

  const page = cmsData || {};

  // SEO
  useEffect(() => {
    if (!page?.title) return;
    document.title = page.metaTitle || `${page.title} — Max Global Software`;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', page.metaDescription || page.tagline || '');
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-32 pb-20">
        <Loader2 className="h-6 w-6 animate-spin text-slatebody/60" />
      </div>
    );
  }

  if (notFound) return <NotFound />;

  return (
    <article>
      {/* ── Hero ── */}
      <section className="relative pt-52 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-sky-100" />
        <div className="absolute inset-0 bg-hero-mesh bg-[length:200%_200%] animate-mesh-pan" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="svcdots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.1" fill="#2F6FB0" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#svcdots)" />
        </svg>

        <div className="container mx-auto relative text-center">
          <motion.nav
            aria-label="Breadcrumb"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex items-center justify-center gap-1 text-[12px] text-slatebody flex-wrap"
          >
            <Link to="/" className="hover:text-brandblue">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
            <Link to="/services" className="hover:text-brandblue">Services</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
            <span className="font-semibold text-navy-deep">{page.crumb || page.title}</span>
          </motion.nav>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1, ease: EASE }}
            className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-navy-deep max-w-3xl mx-auto"
          >
            {page.title}
          </motion.h1>

          {page.tagline && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.25, ease: EASE }}
              className="mt-5 max-w-2xl mx-auto text-slatebody text-sm sm:text-base leading-relaxed"
            >
              {page.tagline}
            </motion.p>
          )}

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            style={{ transformOrigin: 'center' }}
            className="mt-6 h-[2px] w-24 mx-auto rounded-full bg-gradient-to-r from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A]"
          />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45, ease: EASE }}
            className="mt-7 flex flex-wrap items-center justify-center gap-4"
          >
            <PrimaryButton
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start a Project
            </PrimaryButton>
            <BlueAnimatedLink to="/services">
              <ChevronLeft className="h-4 w-4" /> All Services
            </BlueAnimatedLink>
          </motion.div>
        </div>
      </section>

      {/* ── Metrics Bar ── */}
      {Array.isArray(page.metrics) && page.metrics.length > 0 && (
        <MetricsBar metrics={page.metrics} />
      )}

      {/* ── Overview ── */}
      <OverviewSection
        heading={page.overview_heading}
        body={page.overview_body}
        image={page.hero_image}
      />

      {/* ── Features / What We Build ── */}
      {Array.isArray(page.features) && page.features.length > 0 && (
        <FeaturesSection features={page.features} />
      )}

      {/* ── Process ── */}
      {Array.isArray(page.process_steps) && page.process_steps.length > 0 && (
        <ProcessSection steps={page.process_steps} />
      )}

      {/* ── Use Cases ── */}
      {Array.isArray(page.use_cases) && page.use_cases.length > 0 && (
        <UseCasesSection useCases={page.use_cases} />
      )}

      {/* ── Tech Stack ── */}
      {Array.isArray(page.tech_stack) && page.tech_stack.length > 0 && (
        <TechStackSection techStack={page.tech_stack} />
      )}

      {/* ── Benefits / Why MGS ── */}
      {Array.isArray(page.benefits) && page.benefits.length > 0 && (
        <BenefitsSection benefits={page.benefits} />
      )}

      {/* ── Related Services ── */}
      <RelatedServicesSection currentSlug={slug} />

      {/* ── CTA ── */}
      <CtaBand />
    </article>
  );
}
